import React, { useEffect, useState } from 'react';
import { AlertTriangle, FileText, Phone, Check } from 'lucide-react';
const COLORS = [{
  name: 'Dark Bronze',
  hex: '#4A4138'
}, {
  name: 'Black',
  hex: '#222222'
}, {
  name: 'Gold Speck',
  hex: '#C5A059'
}, {
  name: 'Postal Grey',
  hex: '#8A8D8F'
}, {
  name: 'Sandstone',
  hex: '#D2C4A7'
}, {
  name: 'Silver Speck',
  hex: '#B4B7BA'
}, {
  name: 'White',
  hex: '#F8F9FA'
}, {
  name: 'Antique Bronze',
  hex: '#5C4E3A'
}];
const ID_OPTIONS = ['Standard Decal ID', 'Non Standard Decal ID', 'Engraved - No Fill ID'];
interface ProductInfoProps {
  onOptionsChange: (options: any) => void;
}
export function ProductInfo({
  onOptionsChange
}: ProductInfoProps) {
  const [selectedColor, setSelectedColor] = useState(COLORS[0].name);
  const [selectedId, setSelectedId] = useState(ID_OPTIONS[0]);
  const [sequence, setSequence] = useState('');
  const [reviewRequested, setReviewRequested] = useState(false);
  // Notify parent of changes (in a real app, use useEffect)
  const handleOptionChange = (key: string, value: any) => {
    const newOptions = {
      selectedColor,
      selectedId,
      sequence,
      reviewRequested,
      [key]: value
    };
    if (key === 'selectedColor') setSelectedColor(value);
    if (key === 'selectedId') setSelectedId(value);
    if (key === 'sequence') setSequence(value);
    if (key === 'reviewRequested') setReviewRequested(value);
    onOptionsChange(newOptions);
  };
  return <div className="flex flex-col" data-id="element-519">
      {/* Header Info */}
      <div className="mb-6" data-id="element-520">
        <span className="text-sm font-bold text-brand-green uppercase tracking-wider mb-2 block" data-id="element-521">
          Commercial Mailboxes
        </span>
        <h1 className="text-3xl lg:text-4xl font-serif font-bold text-brand-charcoal mb-2" data-id="element-522">
          Versatile 4C Depot 16H - 20 Door Package
        </h1>
        <div className="flex items-center gap-4 text-sm text-gray-500 mb-4" data-id="element-523">
          <span data-id="element-524">
            SKU:{' '}
            <span className="font-mono text-brand-charcoal" data-id="element-525">4C16D-20-D</span>
          </span>
        </div>
        <div className="text-3xl font-bold text-brand-green" data-id="element-526">$4,353.00</div>
      </div>

      {/* Compliance Notice (CRITICAL) */}
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8 flex items-start gap-3" data-id="element-527">
        <AlertTriangle className="text-brand-red shrink-0 mt-0.5" size={20} data-id="element-528" />
        <div data-id="element-529">
          <h4 className="font-bold text-brand-red text-sm mb-1" data-id="element-530">
            Important Compliance Notice
          </h4>
          <p className="text-sm text-red-900 leading-snug" data-id="element-531">
            Depot/freestanding 4C packages are{' '}
            <strong data-id="element-532">not approved for USPS delivery</strong>. Confirm delivery
            use and project requirements before ordering.
          </p>
        </div>
      </div>

      {/* PDF Links */}
      <div className="flex gap-4 mb-8 border-b border-brand-border pb-8" data-id="element-533">
        <a href="#" className="flex items-center gap-2 text-sm font-medium text-brand-charcoal hover:text-brand-green transition-colors" data-id="element-534">
          <FileText size={16} className="text-gray-400" data-id="element-535" /> Product PDF
        </a>
        <a href="#" className="flex items-center gap-2 text-sm font-medium text-brand-charcoal hover:text-brand-green transition-colors" data-id="element-536">
          <FileText size={16} className="text-gray-400" data-id="element-537" /> PDF Line Drawings
        </a>
      </div>

      {/* Options UI */}
      <div className="space-y-8" id="configure-options" data-id="element-538">
        {/* Colors */}
        <div data-id="element-539">
          <div className="flex justify-between items-end mb-3" data-id="element-540">
            <h3 className="font-bold text-brand-charcoal" data-id="element-541">1. Select Color</h3>
            <span className="text-sm text-gray-500" data-id="element-542">{selectedColor}</span>
          </div>
          <div className="flex flex-wrap gap-3" data-id="element-543">
            {COLORS.map(color => <button key={color.name} onClick={() => handleOptionChange('selectedColor', color.name)} className={`w-12 h-12 rounded-full border-2 flex items-center justify-center transition-all ${selectedColor === color.name ? 'border-brand-green scale-110 shadow-md' : 'border-transparent hover:scale-105 shadow-sm'}`} style={{
            backgroundColor: color.hex
          }} title={color.name} data-id="element-544">
                {selectedColor === color.name && <Check size={16} color={['White', 'Sandstone', 'Silver Speck'].includes(color.name) ? '#000' : '#FFF'} data-id="element-545" />}
              </button>)}
          </div>
        </div>

        {/* Door ID */}
        <div data-id="element-546">
          <h3 className="font-bold text-brand-charcoal mb-3" data-id="element-547">
            2. Door Identification
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-548">
            {ID_OPTIONS.map(opt => <button key={opt} onClick={() => handleOptionChange('selectedId', opt)} className={`px-4 py-3 rounded-lg border text-sm font-medium transition-all text-left ${selectedId === opt ? 'border-brand-green bg-brand-green/5 text-brand-green' : 'border-brand-border bg-white text-brand-charcoal hover:border-brand-green/50'}`} data-id="element-549">
                {opt}
              </button>)}
          </div>
        </div>

        {/* Door Sequence */}
        <div data-id="element-550">
          <h3 className="font-bold text-brand-charcoal mb-1" data-id="element-551">
            3. Door Sequence
          </h3>
          <p className="text-xs text-gray-500 mb-3" data-id="element-552">
            Standard numbering will be sequential if none entered below.
          </p>
          <textarea value={sequence} onChange={e => handleOptionChange('sequence', e.target.value)} placeholder="Enter custom sequence or project notes here..." className="w-full p-3 rounded-lg border border-brand-border focus:ring-2 focus:ring-brand-green focus:border-brand-green text-sm min-h-[80px]" data-id="element-553" />
        </div>

        {/* Review Checkbox */}
        <div className="bg-brand-cream p-4 rounded-lg border border-brand-border" data-id="element-554">
          <label className="flex items-start gap-3 cursor-pointer" data-id="element-555">
            <input type="checkbox" checked={reviewRequested} onChange={e => handleOptionChange('reviewRequested', e.target.checked)} className="mt-1 w-4 h-4 text-brand-green border-gray-300 rounded focus:ring-brand-green" data-id="element-556" />
            <div data-id="element-557">
              <span className="font-bold text-brand-charcoal block text-sm" data-id="element-558">
                I want MidAtlantic to review this before ordering.
              </span>
              <span className="text-xs text-gray-600" data-id="element-559">
                Recommended for complex commercial projects to ensure USPS
                compliance and correct configuration.
              </span>
            </div>
          </label>
        </div>
      </div>

      {/* Mobile CTAs (Hidden on Desktop, handled by sticky bar) */}
      <div className="mt-8 flex flex-col gap-3 lg:hidden" data-id="element-560">
        <a href="#configure-options" className="bg-brand-charcoal text-white text-center py-3 rounded-md font-bold" data-id="element-561">
          Configure options
        </a>
        <a href="/ContactUs.html" className="bg-white border border-brand-border text-brand-charcoal text-center py-3 rounded-md font-bold" data-id="element-562">
          Ask before ordering
        </a>
        <a href="tel:301-620-7585" className="text-brand-green text-center py-3 font-bold flex items-center justify-center gap-2" data-id="element-563">
          <Phone size={18} data-id="element-564" /> Call 301-620-7585
        </a>
      </div>
    </div>;
}